import React, { PureComponent, Suspense } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Route, Switch, Redirect } from "react-router-dom";
import { Spin } from "antd";

import routes from "./routes";
import agent from "./agent";
import storage from "./utils/storage";
import { logout, loggedIn, forceLogout } from "./store/actions/authActions";
import { appLoaded, updateCurrentUser } from "./store/actions/commonActions";
import { showErrorMessage } from "./utils/messages";

import "./App.less";

const Login = React.lazy(() => import("./views/Login"));
const Dashboard = React.lazy(() => import("./views/Dashboard"));

/* -------------------- ROYAL SKY BLUE THEME -------------------- */
const styles = {
  appBootWrap: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(180deg, #eff6ff 0%, #ffffff 60%)",
  },
  appBootCard: {
    width: 360,
    maxWidth: "calc(100% - 32px)",
    padding: 18,
    borderRadius: 14,
    background: "linear-gradient(135deg, #ffffff 0%, #f5fbff 100%)",
    border: "1px solid rgba(14,165,233,0.18)",
    boxShadow: "0 12px 34px rgba(37,99,235,0.10)",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    alignItems: "center",
  },
  appBootTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#2563eb",
    letterSpacing: 0.2,
  },
  appBootSub: {
    fontSize: 12,
    color: "rgba(15, 23, 42, 0.60)",
  },
};

class App extends PureComponent {
  componentDidMount() {
    const { appReady, goToLogin, loggedInUser, logoutUser } = this.props;

    const { token, expiry, issuedAt } = storage.getAccesTokenInfo();
    const isTokenExpired = storage.isTokenExpired(expiry, issuedAt);

    if (!token) {
      goToLogin();
      appReady();
      return;
    }

    if (isTokenExpired) {
      agent.Auth.refreshToken(storage.getRefreshTokenInfo().token)
        .then((payload) => {
          // ✅ keep your existing frizza structure (payload.data.*)
          storage.setAccesToken(payload.data.access_token, payload.data.exp);
          agent.setToken(payload.data.access_token);
          loggedInUser();
          appReady();
        })
        .catch((err) => {
          showErrorMessage(err);
          logoutUser();
          appReady();
          throw err;
        });

      return;
    }

    agent.setToken(token);
    loggedInUser();
    appReady();
  }

  renderBoot = (title, sub) => (
    <div style={styles.appBootWrap}>
      <div style={styles.appBootCard}>
        <Spin size="large" />
        <div style={styles.appBootTitle}>{title}</div>
        <div style={styles.appBootSub}>{sub}</div>
      </div>
    </div>
  );

  render() {
    const {
      loggedIn: loggedInState,
      appLoaded: appLoadedState,
      logoutUser,
      appReady,
      updateUser,
    } = this.props;

    if (!appLoadedState) {
      return this.renderBoot("Loading Frizza CMS…", "Checking session");
    }

    return (
      <Switch>
        {loggedInState ? (
          <Route
            path="/"
            render={() => (
              <Suspense
                fallback={this.renderBoot(
                  "Preparing dashboard…",
                  "Almost there"
                )}
              >
                <Dashboard
                  logoutUser={logoutUser}
                  appReady={appReady}
                  updateUser={updateUser}
                  routes={routes}
                />
              </Suspense>
            )}
          />
        ) : (
          <>
            <Route
              path="/"
              render={() => (
                <Suspense
                  fallback={this.renderBoot("Loading login…", "Please wait")}
                >
                  <Login />
                </Suspense>
              )}
            />
            <Redirect to="/" />
          </>
        )}
      </Switch>
    );
  }
}

App.propTypes = {
  loggedIn: PropTypes.bool.isRequired,
  appLoaded: PropTypes.bool.isRequired,
  appReady: PropTypes.func.isRequired,
  goToLogin: PropTypes.func.isRequired,
  loggedInUser: PropTypes.func.isRequired,
  updateUser: PropTypes.func.isRequired,
  logoutUser: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => ({
  appLoaded: state.common.appLoaded,
  loggedIn: state.auth.loggedIn,
  ...ownProps,
});

const mapDispatchToProps = (dispatch) => ({
  appReady: (user) => dispatch(appLoaded(user)),
  updateUser: (user) => dispatch(updateCurrentUser(user)),
  goToLogin: () => dispatch(logout()),
  loggedInUser: () => dispatch(loggedIn()),
  logoutUser: () => dispatch(forceLogout()),
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
